<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type ActaportSettings,
    type ActaportSettingsRequest,
    type CustomerUser,
    RoleEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { toast } from '@/components/ui/toast'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Actaport integration fields
  const isActaportEnabled = ref(false)
  const actaportClientId = ref('')
  const actaportClientSecret = ref('')
  const originalActaportClientSecret = ref('')
  const actaportRealm = ref('')
  const actaportUsername = ref('')
  const actaportPassword = ref('')
  const originalActaportPassword = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description: 'Configure Actaport integration settings for JUPUS.',
      enableActaport: 'Enable Actaport Integration',
      actaportClientId: 'Actaport Client ID',
      actaportClientIdPlaceholder: 'Enter Actaport Client ID',
      actaportClientSecret: 'Actaport Client Secret',
      actaportClientSecretPlaceholder: 'Enter Actaport Client Secret',
      actaportRealm: 'Actaport Realm',
      actaportRealmPlaceholder: 'Enter Actaport Realm',
      actaportUsername: 'Actaport Username',
      actaportUsernamePlaceholder: 'Enter Actaport Username',
      actaportPassword: 'Actaport Password',
      actaportPasswordPlaceholder: 'Enter Actaport Password',
      saving: 'Saving...',
      syncActaport: 'Sync Actaport',
      success: 'Success',
      syncSuccessMsg: 'Actaport was successfully synced.'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: 'Die Actaport Schnittstelle',
      enableActaport: 'Actaport Integration aktivieren',
      actaportClientId: 'Actaport Client ID',
      actaportClientIdPlaceholder: 'Actaport Client ID eingeben',
      actaportClientSecret: 'Actaport Client Secret',
      actaportClientSecretPlaceholder: 'Actaport Client Secret eingeben',
      actaportRealm: 'Actaport Realm',
      actaportRealmPlaceholder: 'Actaport Realm eingeben',
      actaportUsername: 'Actaport Username',
      actaportUsernamePlaceholder: 'Actaport Username eingeben',
      actaportPassword: 'Actaport Passwort',
      actaportPasswordPlaceholder: 'Actaport Passwort eingeben',
      saving: 'Speichern...',
      syncActaport: 'Actaport synchronisieren',
      success: 'Erfolg',
      syncSuccessMsg: 'Actaport wurde erfolgreich synchronisiert.'
    }
  })

  // Validation schema content removed as it is not needed;
  // all fields are optional and have no format rule.
  const validationSchema = {}

  interface FormData {
    actaport_client_id: string
    actaport_client_secret: string
    actaport_realm: string
    actaport_username: string
    actaport_password: string
  }

  const { formData, formErrors, touchedFields, validateField, handleFieldChange } =
    useFormWithValidation<FormData>(
      {
        actaport_client_id: '',
        actaport_client_secret: '',
        actaport_realm: '',
        actaport_username: '',
        actaport_password: ''
      },
      validationSchema
    )

  const internalActaportUpdate = async (data: ActaportSettingsRequest) => {
    if (!user.value) {
      throw Error('User is not defined. Cannot update Actaport settings.')
    }

    await SettingsService.settingsActaportCreate({
      requestBody: data
    })
  }

  const updateActaportSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: ActaportSettingsRequest = {
        is_actaport_export_integration_enabled: isActaportEnabled.value
      }

      // Only include client id if it has a value
      if (actaportClientId.value) {
        data.actaport_client_id = actaportClientId.value
      }

      // Only include client secret if it has changed
      if (actaportClientSecret.value !== originalActaportClientSecret.value) {
        data.actaport_client_secret = actaportClientSecret.value
      }

      // Only include the realm field if it has a value
      if (actaportRealm.value) {
        data.actaport_realm = actaportRealm.value
      }

      // Only include username if it has a value
      if (actaportUsername.value) {
        data.actaport_username = actaportUsername.value
      }

      // Only include password if it has changed
      if (actaportPassword.value !== originalActaportPassword.value) {
        data.actaport_password = actaportPassword.value
      }

      await internalActaportUpdate(data)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const actaportSettings: ActaportSettings =
        (await SettingsService.settingsActaportList()) as ActaportSettings

      // Initialize form with existing values if available
      if (actaportSettings) {
        isActaportEnabled.value = actaportSettings.is_actaport_export_integration_enabled || false
        actaportClientId.value = actaportSettings.actaport_client_id || ''
        formData.actaport_client_id = actaportClientId.value
        actaportClientSecret.value = actaportSettings.actaport_client_secret || ''
        originalActaportClientSecret.value = actaportSettings.actaport_client_secret || ''
        formData.actaport_client_secret = actaportClientSecret.value
        actaportRealm.value = actaportSettings.actaport_realm || ''
        formData.actaport_realm = actaportRealm.value
        actaportUsername.value = actaportSettings.actaport_username || ''
        formData.actaport_username = actaportUsername.value
        actaportPassword.value = actaportSettings.actaport_password || ''
        originalActaportPassword.value = actaportSettings.actaport_password || ''
        formData.actaport_password = actaportPassword.value
      }
    } catch (error) {
      console.error('Error loading Actaport settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isActaportEnabled], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateActaportSettings()
    }
  })

  // Add watchers for the input fields
  watch(actaportClientId, (newValue) => {
    formData.actaport_client_id = newValue
    handleFieldChange('actaport_client_id', newValue)
    if (user.value) {
      updateActaportSettings()
    }
  })

  watch(actaportClientSecret, (newValue) => {
    formData.actaport_client_secret = newValue
    handleFieldChange('actaport_client_secret', newValue)
    if (user.value) {
      updateActaportSettings()
    }
  })

  watch(actaportRealm, (newValue) => {
    formData.actaport_realm = newValue
    handleFieldChange('actaport_realm', newValue)
    if (user.value) {
      updateActaportSettings()
    }
  })

  watch(actaportUsername, (newValue) => {
    formData.actaport_username = newValue
    handleFieldChange('actaport_username', newValue)
    if (user.value) {
      updateActaportSettings()
    }
  })

  watch(actaportPassword, (newValue) => {
    formData.actaport_password = newValue
    handleFieldChange('actaport_password', newValue)
    if (user.value) {
      updateActaportSettings()
    }
  })

  const isActaportSyncable = computed(() => {
    return (
      actaportClientId.value &&
      actaportClientSecret.value &&
      actaportRealm.value &&
      actaportUsername.value &&
      actaportPassword.value
    )
  })

  const syncActaport = async () => {
    try {
      await SettingsService.settingsActaportSyncUpdate()
      toast({
        title: t('success'),
        description: t('syncSuccessMsg'),
        variant: 'default'
      })
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: 'Error',
        description: 'Actaport snyc failed',
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== RoleEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- Actaport Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable Actaport Integration -->
          <CheckboxField
            v-model="isActaportEnabled"
            name="is_actaport_export_integration_enabled"
            :label="t('enableActaport')"
          />
          <!-- Actaport settings (only shown if Actaport is enabled) -->
          <div v-if="isActaportEnabled">
            <InputField
              v-model="actaportClientId"
              name="actaport_client_id"
              :label="t('actaportClientId')"
              :placeholder="t('actaportClientIdPlaceholder')"
            />

            <InputField
              v-model="actaportClientSecret"
              name="actaport_client_secret"
              :label="t('actaportClientSecret')"
              :placeholder="t('actaportClientSecretPlaceholder')"
              type="password"
            />

            <InputField
              v-model="actaportRealm"
              name="actaport_realm"
              :label="t('actaportRealm')"
              :placeholder="t('actaportRealmPlaceholder')"
            />

            <InputField
              v-model="actaportUsername"
              name="actaport_username"
              :label="t('actaportUsername')"
              :placeholder="t('actaportUsernamePlaceholder')"
            />

            <InputField
              v-model="actaportPassword"
              name="actaport_password"
              :label="t('actaportPassword')"
              :placeholder="t('actaportPasswordPlaceholder')"
              type="password"
            />

            <button
              class="mt-5 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              :disabled="!isActaportSyncable"
              @click="syncActaport"
            >
              {{ t('syncActaport') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
